import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { buyersApi, type BuyerLeadSource } from "@/api/buyers";
import { useToast } from "@/design-system/components/ui";
import { buyerKeys } from "@/hooks/buyers/buyerKeys";
import { buyerLabel } from "./buyerFilters";

/**
 * Lead source, editable in place (PR 3b).
 *
 * <p><b>Manually set by the specialist, never inferred.</b> The funnel captures
 * no marketing attribution — nothing on the finalize request carries a UTM or a
 * campaign — so this is somebody recording what they know, and the honest way to
 * collect that is to ask.</p>
 *
 * <p><b>Deliberately coarse.</b> <c>SourceDetail</c> is free text alongside it
 * and is where "referred by Dave at Acme" or a campaign name goes. Splitting the
 * enum per platform would push a taxonomy decision onto every specialist at the
 * moment they are trying to record one fact, and the answers would not be
 * comparable anyway.</p>
 *
 * <p><b>An inline field rather than a form.</b> There is no buyer edit form on
 * this page — every other attribution field renders read-only — so a dropdown
 * that saves on change is the whole feature rather than the first slice of one.
 * The PATCH endpoint and its <c>Source</c> field already existed.</p>
 */

/** Grouped for scanning, not for storage. The groups are presentation only —
 *  the wire value is the bare enum member. */
const SOURCE_GROUPS: ReadonlyArray<{
  label: string;
  options: readonly BuyerLeadSource[];
}> = [
  // 🔴 A source missing from these groups renders nowhere in this dropdown,
  // silently — the type union does not enforce coverage.
  { label: "Paid", options: ["PaidSocial", "PaidSearch", "YouTubeLandingPage"] },
  {
    label: "Inbound",
    options: ["Organic", "PrequalificationForm", "InboundCall", "InboundEmail"],
  },
  { label: "Relationship", options: ["Referral", "BusinessBroker", "ListingSite", "Event"] },
  { label: "System", options: ["PurchasedList", "ManualEntry", "Other"] },
];

/** Only where `buyerLabel`'s PascalCase split is not enough on its own. */
const SOURCE_LABELS: Partial<Record<BuyerLeadSource, string>> = {
  PaidSocial: "Paid social",
  PaidSearch: "Paid search",
  Organic: "Website form",
  PrequalificationForm: "Prequalification funnel",
  BusinessBroker: "Business broker or listing site",
  PurchasedList: "CSV import",
  ManualEntry: "Manual entry",
  // The splitter would render this "You Tube Landing Page".
  YouTubeLandingPage: "YouTube Landing Page",
  ListingSite: "Listing site",
};

function sourceLabel(s: BuyerLeadSource): string {
  return SOURCE_LABELS[s] ?? buyerLabel(s);
}

export function LeadSourceField({
  buyerLeadId,
  value,
  canEdit,
}: {
  buyerLeadId: string;
  value: BuyerLeadSource;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [pending, setPending] = useState<BuyerLeadSource | null>(null);

  const save = useMutation({
    mutationFn: (source: BuyerLeadSource) => buyersApi.update(buyerLeadId, { source }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
      toast.success("Lead source updated.");
    },
    onError: (e: Error) => {
      // Snap back to the server's value — leaving the control showing a choice
      // that did not save is worse than not offering the control.
      setPending(null);
      toast.error(e.message);
    },
  });

  if (!canEdit) {
    return <span className="text-small text-ink">{sourceLabel(value)}</span>;
  }

  return (
    <select
      data-testid="buyer-lead-source"
      aria-label="Lead source"
      className="rounded-sm border border-gray-mid bg-white-pure px-space-2 py-[4px] text-small text-ink"
      value={pending ?? value}
      disabled={save.isPending}
      onChange={(e) => {
        const next = e.target.value as BuyerLeadSource;
        setPending(next);
        save.mutate(next);
      }}
    >
      {/* Ungrouped: it is the absence of an answer, not a kind of answer. */}
      <option value="Unknown">{sourceLabel("Unknown")}</option>
      {SOURCE_GROUPS.map((g) => (
        <optgroup key={g.label} label={g.label}>
          {g.options.map((s) => (
            <option key={s} value={s}>
              {sourceLabel(s)}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}
